import { useCallback, useEffect } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Y200 } from '@atlaskit/theme/colors';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { SupportedThirdParty } from '../../../../common/constants/import-type';
import type { RequestAccessTokenMethod, ThirdPartyEntity } from '../../../../common/types';
import { useContentForThirdParty } from '../../../../common/utils';
import { useThirdPartyState } from '../../../../controllers/import-state';
import { useThirdPartySpaces } from '../../../../services/third-party-spaces';

type Props = {
	thirdParty: SupportedThirdParty;
	shouldShowPlaceholderIcons: boolean;
	requestAuthenticationToken: RequestAccessTokenMethod;
	shouldShowSpaceSelector: boolean;
};

export const useSpaces = ({
	thirdParty,
	shouldShowPlaceholderIcons,
	requestAuthenticationToken,
	shouldShowSpaceSelector,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		spaceSelectorLabel: label,
		spaceSelectorPlaceholder: placeholder,
		errorOccurredFetchingSpacesHeader: errorOccurredHeader,
		noSpacesFoundHeader,
		noSpacesFoundDescription,
	} = useContentForThirdParty(thirdParty);

	const { fetchThirdPartySpaces, hasErrorOccurred, isLoading } = useThirdPartySpaces({
		thirdParty,
		requestAuthenticationToken,
	});

	const [{ spaces, selectedWorkspaceId, selectedSpaceId }, { setThirdPartyState }] =
		useThirdPartyState();

	const onFetchSpaces = useCallback(() => {
		if (selectedWorkspaceId === null || spaces != null) {
			return;
		}

		fetchThirdPartySpaces(selectedWorkspaceId).then((spaceEntities) => {
			setThirdPartyState({
				spaces: spaceEntities,
			});

			fireUIAnalytics(createAnalyticsEvent({}), 'thirdPartySpaces retrieved', {
				thirdParty,
				numSpaces: spaceEntities?.length || 0,
			});
		});
	}, [
		createAnalyticsEvent,
		fetchThirdPartySpaces,
		spaces,
		selectedWorkspaceId,
		setThirdPartyState,
		thirdParty,
	]);

	useEffect(() => {
		shouldShowSpaceSelector && onFetchSpaces();
	}, [onFetchSpaces, shouldShowSpaceSelector]);

	// TODO: Remove this on cleanup of 'jira_importer_third_party_state_refactor_2' feature gate
	const onChangeOld = useCallback(
		(spaceId: string | null, spaceName: string | null) => {
			setThirdPartyState({
				selectedSpaceId: spaceId,
				selectedSpaceName: spaceName,
				selectedFolderId: null,
				selectedSubfolderId: null,
				selectedEntities: null,
				selectedEntityId: null,
				selectedEntityName: null,
				selectedEntityStyle: null,
				folders: null,
				subfolders: null,
				entities: null,
				entityStats: null,
				members: null,
				thirdPartyFieldConfigState: null,
				thirdPartyStatusMapping: null,
			});
		},
		[setThirdPartyState],
	);

	const onChange = useCallback(
		(selectedSpace: ThirdPartyEntity | null) => {
			setThirdPartyState({
				selectedSpaceId: selectedSpace ? selectedSpace.id : null,
				selectedSpaceName: selectedSpace ? selectedSpace.name : null,
				selectedFolderId: null,
				selectedSubfolderId: null,
				selectedEntities: null,
				selectedEntityId: null,
				selectedEntityName: null,
				selectedEntityStyle: null,
				folders: null,
				subfolders: null,
				entities: null,
				entityStats: null,
				members: null,
				thirdPartyFieldConfigState: null,
				thirdPartyStatusMapping: null,
			});
		},
		[setThirdPartyState],
	);

	return {
		label,
		placeholder,
		starredOptionsLabel: '',
		otherOptionsLabel: '',
		noEntitiesFoundHeader: noSpacesFoundHeader,
		noEntitiesFoundDescription: noSpacesFoundDescription,
		errorOccurredHeader,
		entities: spaces,
		selectedEntityId: selectedSpaceId,
		isLoading,
		isDisabled: selectedWorkspaceId === null,
		isClearable: false,
		hasErrorOccurred,
		onChangeOld,
		onChange,
		onRetry: onFetchSpaces,
		placeholderColor: shouldShowPlaceholderIcons ? Y200 : undefined,
		isEmptyEntities: !spaces || spaces.length === 0,
		preselectOnlyOption: true,
	};
};
